import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { ArrowRightIcon, ScrollTextIcon, TicketIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import {
  CAMPAIGN_TERMS_OF_PARTICIPATION,
  MAX_FILE_SIZE,
} from '../../../../utils/constants.js';
import { scrollToId } from '../../../../utils/scroll.js';

import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import { Input } from '../../../ui/input.jsx';
import PortalBigButton from '../big-button.jsx';
import { campaignDateMs } from '../campaign-countdown.jsx';

const CAMPAIGN_CLOSED_MESSAGE =
  'A campanha fechou ao público. Já não é possível submeter faturas.';

const initalFormValues = {
  // code: '',
  // issuedDate: '',
  // shopId: '',
  invoiceFile: '',
  consentTermsOfParticipation: null,
  status: 'Pendente',
};

export default function FormJoinCampaign({
  queryActiveCampaign,
  scrollId,
  onCampaignClosed,
}) {
  const { user } = useSnapshot(authState);
  const inputFileRef = useRef(null);

  const [chooseForm, setChooseForm] = useState();

  const joinCampaignForm = useForm({
    mode: 'onChange',
    defaultValues: initalFormValues,
    resolver: yupResolver(
      yup.object().shape({
        invoiceFile: yup
          .mixed()
          .test('required', 'A fotografia da fatura é obrigatória', (file) => {
            return file && file.size;
          })
          .test(
            'fileType',
            'O ficheiro inserido não é uma imagem (tipo de imagens válidas: png, pdf, jpg e jpeg)',
            (value) => {
              return (
                value &&
                ['png', 'pdf', 'jpg', 'jpeg', 'webp'].indexOf(
                  value.name.toLowerCase().split('.').pop(),
                ) > -1
              );
            },
          )
          .test(
            'fileSize',
            'O ficheiro inserido ultrapassa o limite de 15MB',
            (file) => {
              // console.log('file.size', file.size);
              return file && file.size < MAX_FILE_SIZE;
            },
          ),
      }),
    ),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async (data) => {
      // console.log('data', data);

      const formData = new FormData();
      formData.append('invoiceFile', data.invoiceFile);

      data.amount = 0;
      data.campaignId = queryActiveCampaign?.data?.ROWID;

      if (user?.type !== 'Customer') {
        throw Error('Account is not a Customer');
      }

      data.customerId = user.details.ROWID;
      data.createdIn = 'Portal do Cliente';

      const newData = { ...data };
      delete newData.invoiceFile;

      formData.append('data', JSON.stringify(newData));

      const { data: newInvoice } = await msCatalystApi.post(
        `customers/invoices`,
        formData,
      );
      return newInvoice.data;
    },
    onSuccess: async () => {
      toast.success('Participação registada com successo!');

      if (!user.details.consentTermsOfParticipation) {
        authState.user.details.consentTermsOfParticipation = true;
      }

      setChooseForm(null);

      joinCampaignForm.reset(initalFormValues);
      scrollToId(scrollId);
    },
    onError: (err) => {
      if (err?.response?.data?.code === 'CAMPAIGN_CLOSED') {
        toast.error(CAMPAIGN_CLOSED_MESSAGE);
        onCampaignClosed();
      } else if (
        err?.response?.data?.message ===
        'Duplicate value for uniqueCode. Please give a different value'
      ) {
        toast.error(`Fatura duplicada - Esta fatura já existe no sistema`);
      } else {
        toast.error(`Erro ao registar participação`);
      }
    },
  });

  const onFormSubmit = async (data) => {
    const closeMs = campaignDateMs(queryActiveCampaign?.data?.closeDate);
    if (!Number.isFinite(closeMs) || Date.now() >= closeMs) {
      toast.error(CAMPAIGN_CLOSED_MESSAGE);
      onCampaignClosed();
      return;
    }

    await createInvoiceMutation.mutateAsync(data);
  };

  return (
    <>
      {!chooseForm && (
        <>
          <PortalBigButton
            icon={<ScrollTextIcon />}
            title="Submeter fatura(s)"
            onClick={() => {
              scrollToId(scrollId);
              setChooseForm('invoice');
            }}
          />
          <Link to="/portal/coupons">
            <PortalBigButton icon={<TicketIcon />} title="Ver os meus cupões" />
          </Link>
        </>
      )}
      {chooseForm && !queryActiveCampaign.isLoading && (
        <Form {...joinCampaignForm}>
          <form onSubmit={joinCampaignForm.handleSubmit(onFormSubmit)}>
            <div className="flex flex-col space-y-4">
              <div className="flex flex-col gap-5">
                <FormField
                  control={joinCampaignForm.control}
                  name="invoiceFile"
                  rules={{
                    required: 'A fotografia da fatura é obrigatória',
                    validate: {
                      fileSize: (file) => {
                        if (!file || !file.size) {
                          return 'A fotografia da fatura é obrigatória';
                        }
                        if (file.size >= 10000000) {
                          return 'O ficheiro inserido ultrapassa o limite de 10MB';
                        }
                        return true;
                      },
                      fileType: (file) => {
                        if (!file || !file.name) {
                          return true;
                        }
                        const extension = file.name
                          .toLowerCase()
                          .split('.')
                          .pop();
                        if (
                          !['png', 'pdf', 'jpg', 'jpeg', 'webp'].includes(
                            extension,
                          )
                        ) {
                          return 'O ficheiro inserido não é uma imagem (tipo de imagens válidas: png, pdf, jpg e jpeg)';
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <FormItem variant="portal">
                        <FormLabel
                          className="!font-lively !text-base font-bold"
                          required={true}
                        >
                          Comprovativo da fatura
                        </FormLabel>
                        <div className="flex min-h-[46px] w-full items-center text-wrap rounded-full border border-input border-portal-foreground !bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent">
                          <Button
                            variant="ghost"
                            type="button"
                            className="mr-2 h-auto px-2 py-1"
                            onClick={() => inputFileRef.current?.click()}
                          >
                            Carregar ficheiro
                          </Button>

                          <span>
                            {field.value
                              ? field.value?.name
                              : 'Não foi carregado nenhum ficheiro'}
                          </span>
                        </div>

                        <FormControl>
                          <Input
                            type="file"
                            ref={inputFileRef}
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={async (ev) => {
                              const files = ev.target.files;

                              joinCampaignForm.setValue(
                                'invoiceFile',
                                files?.[0],
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );
                            }}
                          />
                        </FormControl>

                        <FormMessage />

                        <div className="text-sm">
                          <p>
                            A imagem não pode ultrapassar os <b>15MB</b>.
                            Formatos permitidos: <b>pdf, png, jpg ou jpeg</b>.
                          </p>
                          <hr className="my-2 border-t border-gray-200" />
                          <p className="mb-1 font-bold">
                            A imagem deverá ter os seguintes aspetos legíveis:
                          </p>
                          <ul className="list-disc pl-5">
                            <li>Nome da loja</li>
                            <li>Data da compra</li>
                            <li>Valor total da compra</li>
                            <li>Número da fatura</li>
                            <li>Número de contribuinte do estabelecimento</li>
                            <li>Número de contribuinte do cliente</li>
                          </ul>
                        </div>
                      </FormItem>
                    );
                  }}
                />

                {!user.details.consentTermsOfParticipation ? (
                  <FormField
                    control={joinCampaignForm.control}
                    name="consentTermsOfParticipation"
                    rules={{
                      required: 'Deve aceitar os termos para participar',
                      validate: (value) =>
                        value === true ||
                        'Deve aceitar os termos para participar',
                    }}
                    render={({ field }) => (
                      <FormItem variant="portal">
                        <FormLabel
                          className="!font-lively !text-base font-bold"
                          required={true}
                        >
                          Condições de participação
                        </FormLabel>
                        <div className="flex min-h-10 flex-row items-center space-x-2.5">
                          <FormControl>
                            <Checkbox
                              className="size-6"
                              id="consentTermsOfParticipation"
                              checked={field.value}
                              onCheckedChange={(checked) =>
                                field.onChange(checked)
                              }
                            />
                          </FormControl>
                          <span className="text-xs text-portal-text">
                            {CAMPAIGN_TERMS_OF_PARTICIPATION}
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
              </div>
            </div>

            <div className="mt-10 flex flex-col justify-end gap-4 sm:flex-row sm:items-center sm:gap-2 lg:justify-between">
              <Button
                type="button"
                variant="portal-destructive"
                onClick={() => {
                  setTimeout(() => {
                    joinCampaignForm.reset(initalFormValues);
                  }, 1000);
                  scrollToId(scrollId);
                  setChooseForm(null);
                }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="portal"
                disabled={
                  !joinCampaignForm.formState.isValid ||
                  joinCampaignForm.formState.isSubmitting ||
                  !joinCampaignForm.formState.isDirty
                }
                isLoading={joinCampaignForm.formState.isSubmitting}
              >
                Participar
                <ArrowRightIcon />
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}
